import type { LucideIcon } from 'lucide-react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Spinner } from './Spinner.tsx';

export type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type Size = 'sm' | 'md' | 'lg';

const variants: Record<Variant, string> = {
  primary: 'bg-accent text-accent-ink hover:bg-accent-pressed active:bg-accent-pressed',
  secondary: 'bg-surface3 text-text border border-border-strong hover:bg-secondary-pressed active:bg-secondary-pressed',
  ghost: 'bg-transparent text-text hover:bg-white/6 active:bg-white/6',
  danger: 'bg-danger-soft text-danger hover:bg-[rgba(255,100,100,0.2)] active:bg-[rgba(255,100,100,0.2)]',
};
const sizes: Record<Size, string> = {
  sm: 'h-[34px] px-3 t-label',
  md: 'h-11 px-4 t-heading',
  lg: 'h-[52px] px-4 t-heading',
};

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: Variant;
  size?: Size;
  icon?: LucideIcon;
  loading?: boolean;
  children?: ReactNode;
  full?: boolean;
}

/** docs/spec/06 §2 Button. */
export function Button({
  variant = 'secondary',
  size = 'md',
  icon: Icon,
  loading,
  children,
  full,
  disabled,
  className = '',
  ...rest
}: ButtonProps) {
  const iconSize = size === 'sm' ? 16 : 18;
  return (
    <button
      type="button"
      {...rest}
      disabled={disabled || loading}
      className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-md whitespace-nowrap transition-colors disabled:opacity-45 ${variants[variant]} ${sizes[size]} ${full ? 'w-full' : ''} ${className}`}
    >
      {loading ? <Spinner size={iconSize} /> : Icon ? <Icon size={iconSize} strokeWidth={2.2} aria-hidden /> : null}
      {children}
    </button>
  );
}
